import { useState, useEffect } from 'react'
import { ListChecks, FlaskConical, Check, Droplets, Clock, Beaker, RotateCcw } from 'lucide-react'
import { useLab } from '../../../context/LabContext'
import { VolumetricFlask } from '../../lab'
import InfoBox from '../InfoBox'

const steps = [
  { id: 1, title: 'Prepare Stock Solution', short: 'Weigh ~0.1360 g KH₂PO₄, dissolve in 1 L' },
  { id: 2, title: 'Prepare 2.0×10⁻⁵ M Std', short: 'Click Std 1 flask (100 mL)' },
  { id: 3, title: 'Prepare 5.0×10⁻⁵ M Std', short: 'Click Std 2 flask (100 mL)' },
  { id: 4, title: 'Prepare 1.0×10⁻⁴ M Std', short: 'Click Std 3 flask (100 mL)' },
  { id: 5, title: 'Prepare 5.0×10⁻⁴ M Std', short: 'Click Std 4 flask (100 mL)' },
  { id: 6, title: 'Prepare 7.5×10⁻⁴ M Std', short: 'Click Std 5 flask (100 mL)' },
  { id: 7, title: 'Mix with Vanadomolybdate', short: '10 mL phosphate + 5 mL reagent each' },
  { id: 8, title: 'Wait for Color', short: 'Wait 10 minutes for color development' }
]

const TOTAL_STEPS = steps.length

// Initial state for exp3Part2 - used for reset
const INITIAL_EXP3_PART2 = {
  stockPrepared: false,
  standards: [
    { conc: 0.00002, label: '2.0×10⁻⁵ M', prepared: false, absorbance: null },
    { conc: 0.00005, label: '5.0×10⁻⁵ M', prepared: false, absorbance: null },
    { conc: 0.0001, label: '1.0×10⁻⁴ M', prepared: false, absorbance: null },
    { conc: 0.0005, label: '5.0×10⁻⁴ M', prepared: false, absorbance: null },
    { conc: 0.00075, label: '7.5×10⁻⁴ M', prepared: false, absorbance: null }
  ],
  reagentAdded: false,
  colorDeveloped: false,
  colorDevelopmentProgress: 0
}

// Color gradient for standards (pale to deep yellow) based on concentration
const getStandardColor = (conc: number, colorDeveloped: boolean): string => {
  if (!colorDeveloped) return 'rgba(200, 220, 240, 0.3)' // Clear/water color
  const intensity = conc / 0.00075 // Normalize to max concentration
  const r = 255
  const g = 253 - Math.round(intensity * (253 - 230))
  const b = 231 - Math.round(intensity * (231 - 120))
  return `rgb(${r}, ${g}, ${b})`
}

const Part2Standards = () => {
  const { state, updateExp3Part2, completeStep, update } = useLab()
  const { exp3Part2, currentStep, labMode, completedSteps } = state

  const [showInfo, setShowInfo] = useState<number | null>(null)
  const [timerRunning, setTimerRunning] = useState(false)
  const [timerValue, setTimerValue] = useState(0)
  const [addingReagent, setAddingReagent] = useState<number | null>(null)

  // Detect and fix state inconsistencies on mount.
  // If completedSteps says steps are done but exp3Part2 data disagrees, reset.
  useEffect(() => {
    const stepsCompleted = steps.filter(s => completedSteps.includes(`standards-${s.id}`))
    const allStepsDone = stepsCompleted.length === steps.length

    // Detect deadlock: all steps "completed" but part isn't actually finished
    if (allStepsDone && !exp3Part2.colorDeveloped) {
      // State is inconsistent - clear standards-* steps and reset Part 2 data
      const otherSteps = completedSteps.filter((s: string) => !s.startsWith('standards-'))
      update({
        completedSteps: otherSteps,
        currentStep: 1
      })
      updateExp3Part2(INITIAL_EXP3_PART2)
      return
    }

    // Detect partial inconsistency: stock "completed" but stockPrepared is false
    if (completedSteps.includes('standards-1') && !exp3Part2.stockPrepared) {
      const otherSteps = completedSteps.filter((s: string) => !s.startsWith('standards-'))
      update({
        completedSteps: otherSteps,
        currentStep: 1
      })
      updateExp3Part2(INITIAL_EXP3_PART2)
      return
    }

    // Normal initialization: set currentStep to first incomplete step
    if (labMode === 'guided') {
      const firstIncomplete = steps.find(s => !completedSteps.includes(`standards-${s.id}`))
      const targetStep = firstIncomplete ? firstIncomplete.id : steps[steps.length - 1].id
      if (currentStep !== targetStep) {
        update({ currentStep: targetStep })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset handler for when user is stuck
  const handleResetPart2 = () => {
    const otherSteps = completedSteps.filter((s: string) => !s.startsWith('standards-'))
    update({
      completedSteps: otherSteps,
      currentStep: 1
    })
    updateExp3Part2(INITIAL_EXP3_PART2)
    setTimerRunning(false)
    setTimerValue(0)
    setAddingReagent(null)
  }

  const isActive = (s: number) => labMode === 'free' || currentStep === s
  const isDone = (s: number) => completedSteps.includes(`standards-${s}`)

  // Color development timer effect
  useEffect(() => {
    if (!timerRunning || exp3Part2.colorDeveloped) return
    const t = setInterval(() => {
      setTimerValue(v => {
        const newVal = v + 1
        const progress = Math.min((newVal / 30) * 100, 100) // 30 seconds for demo

        updateExp3Part2({ colorDevelopmentProgress: progress })

        if (progress >= 100) {
          updateExp3Part2({ colorDeveloped: true })
          setTimerRunning(false)
          completeStep('standards-8')
        }

        return newVal
      })
    }, 100) // Fast for demo
    return () => clearInterval(t)
  }, [timerRunning, exp3Part2.colorDeveloped, updateExp3Part2, completeStep])

  // Handlers
  const handlePrepareStock = () => {
    if (!isActive(1) || isDone(1)) return
    updateExp3Part2({ stockPrepared: true })
    completeStep('standards-1')
  }

  const handlePrepareStandard = (index: number) => {
    const stepNum = index + 2 // Flasks map to steps 2-6
    if (!isActive(stepNum)) return
    if (!exp3Part2.stockPrepared) return // Must prepare stock first
    if (index > 0 && !exp3Part2.standards[index - 1].prepared) return // Must prepare in order

    const newStandards = [...exp3Part2.standards]
    newStandards[index] = { ...newStandards[index], prepared: true }
    updateExp3Part2({ standards: newStandards })
    completeStep(`standards-${stepNum}`)
  }

  const handleAddReagent = () => {
    if (!isActive(7)) return
    // Check all standards are prepared
    const allPrepared = exp3Part2.standards.every((s: { prepared: boolean }) => s.prepared)
    if (!allPrepared) return

    // Animate adding reagent to each flask
    let index = 0
    const addToNext = () => {
      if (index < exp3Part2.standards.length) {
        setAddingReagent(index)
        setTimeout(() => {
          setAddingReagent(null)
          index++
          addToNext()
        }, 400)
      } else {
        updateExp3Part2({ reagentAdded: true })
        completeStep('standards-7')
      }
    }
    addToNext()
  }

  const handleStartColorDevelopment = () => {
    if (!isActive(8) || !exp3Part2.reagentAdded) return
    setTimerRunning(true)
  }

  const completedCount = completedSteps.filter((s: string) => s.startsWith('standards')).length

  // Calculate partial color development
  const getPartialColor = (conc: number): string => {
    if (!exp3Part2.reagentAdded) return 'rgba(200, 220, 240, 0.3)'
    if (exp3Part2.colorDeveloped) return getStandardColor(conc, true)

    // Interpolate color based on development progress
    const progress = exp3Part2.colorDevelopmentProgress / 100
    const finalColor = getStandardColor(conc, true)

    // Simple interpolation - just return the final color with opacity
    return finalColor.replace('rgb', 'rgba').replace(')', `, ${0.3 + progress * 0.7})`)
  }

  return (
    <div className="part2-standards">
      {/* Steps Panel */}
      <div className="steps-panel">
        <div className="steps-header">
          <ListChecks size={16} />
          <h3>Part II: Standard Preparation</h3>
        </div>
        <div className="steps-list">
          {steps.map(s => (
            <div
              key={s.id}
              className={`step-item ${isActive(s.id) && !isDone(s.id) ? 'active' : ''}`}
            >
              <div className={`step-number ${isDone(s.id) ? 'done' : isActive(s.id) ? 'current' : ''}`}>
                {isDone(s.id) ? <Check size={12} /> : s.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className={`step-title ${isActive(s.id) ? 'highlight' : ''}`}>
                    {s.title}
                  </span>
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(s.id) }}
                  >
                    ?
                  </button>
                </div>
                {isActive(s.id) && !isDone(s.id) && (
                  <span className="step-hint">{s.short}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="lab-bench">
        <div className="bench-header">
          <div className="bench-title">
            <FlaskConical size={16} />
            <h3>Lab Bench</h3>
          </div>
          <span className="bench-progress">{completedCount}/{TOTAL_STEPS} Complete</span>
          <button className="reset-part-btn" onClick={handleResetPart2} title="Reset Part 2">
            <RotateCcw size={14} /> Reset
          </button>
        </div>

        <div className="bench-area">
          {/* Color development progress */}
          {timerRunning && !exp3Part2.colorDeveloped && (
            <div className="color-progress">
              <div
                className="color-bar"
                style={{ width: `${exp3Part2.colorDevelopmentProgress}%` }}
              />
              <span>Color Development: {Math.round(exp3Part2.colorDevelopmentProgress)}%</span>
            </div>
          )}

          {/* Equipment Layout */}
          <div className="equipment-grid">
            {/* Step 1: Prepare Stock Solution Button */}
            {!isDone(1) && (
              <button
                className={`action-button stock-button ${isActive(1) ? 'equipment-active' : ''}`}
                onClick={handlePrepareStock}
                disabled={!isActive(1)}
              >
                <Beaker size={18} />
                Prepare 1.0{'×'}10{'⁻\u00b3'} M Stock Solution
              </button>
            )}
            {isDone(1) && !exp3Part2.standards.every((s: { prepared: boolean }) => s.prepared) && (
              <div className="stock-done-badge">
                <Check size={14} /> Stock Solution Ready (1.0{'×'}10{'⁻\u00b3'} M)
              </div>
            )}

            {/* Volumetric Flasks Row */}
            <div className="flasks-row">
              {exp3Part2.standards.map((standard: { conc?: number; ppm?: number; label?: string; prepared: boolean }, index: number) => {
                const stepNum = index + 2
                const conc = standard.conc ?? (standard.ppm ? standard.ppm * 0.00015 : 0)
                const canClick = isActive(stepNum) && !isDone(stepNum) &&
                  exp3Part2.stockPrepared &&
                  (index === 0 || exp3Part2.standards[index - 1].prepared)

                return (
                  <div key={index} className="flask-container">
                    <VolumetricFlask
                      size={100}
                      fillLevel={standard.prepared ? 75 : 0}
                      fillColor={getPartialColor(conc)}
                      active={canClick}
                      onClick={canClick ? () => handlePrepareStandard(index) : undefined}
                      disabled={!canClick && !standard.prepared}
                      label={standard.label || `Std ${index + 1}`}
                    />
                    {addingReagent === index && (
                      <div className="reagent-drop">
                        <Droplets size={20} />
                      </div>
                    )}
                    {standard.prepared && (
                      <div className="flask-check">
                        <Check size={14} />
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Unknown sample flask */}
              <div className="flask-container unknown">
                <VolumetricFlask
                  size={100}
                  fillLevel={75}
                  fillColor={exp3Part2.colorDeveloped ? '#D4A574' : '#3D2314'}
                  disabled={true}
                  label="Unknown"
                />
                <span className="flask-sublabel">(Cola Sample)</span>
              </div>
            </div>

            {/* Reagent Bottle */}
            <div className="reagent-section">
              <svg
                viewBox="0 0 80 120"
                width={80}
                height={120}
                onClick={handleAddReagent}
                style={{
                  cursor: isActive(7) && !isDone(7) && exp3Part2.standards.every((s: { prepared: boolean }) => s.prepared)
                    ? 'pointer' : 'default',
                  opacity: isDone(7) ? 0.5 : 1
                }}
                className={isActive(7) && !isDone(7) ? 'equipment-active' : ''}
              >
                {/* Reagent bottle body */}
                <rect x="15" y="30" width="50" height="70" fill="#4A3728" rx="5" />
                <rect x="15" y="30" width="50" height="15" fill="#5A4738" rx="5" />
                {/* Neck */}
                <rect x="28" y="15" width="24" height="18" fill="#3A2718" rx="2" />
                {/* Cap */}
                <rect x="25" y="5" width="30" height="12" fill="#1A1A1A" rx="3" />
                {/* Label */}
                <rect x="20" y="50" width="40" height="35" fill="#F5F5DC" rx="2" />
                <text x="40" y="63" textAnchor="middle" fontSize="6" fill="#333">Vanado-</text>
                <text x="40" y="72" textAnchor="middle" fontSize="6" fill="#333">molybdate</text>
                <text x="40" y="81" textAnchor="middle" fontSize="5" fill="#666">Reagent</text>
                {/* Liquid inside */}
                <rect x="18" y="55" width="44" height="42" fill="#FFE135" opacity="0.6" rx="3" />
              </svg>
              <span className="equipment-label">Vanadomolybdate Reagent</span>
            </div>

            {/* Start Color Development Button */}
            {exp3Part2.reagentAdded && !exp3Part2.colorDeveloped && !timerRunning && (
              <button
                className="action-button timer-button"
                onClick={handleStartColorDevelopment}
              >
                <Clock size={18} />
                Start Color Development (10 min)
              </button>
            )}

            {/* Completion message */}
            {exp3Part2.colorDeveloped && (
              <div className="completion-message">
                <Check size={24} />
                <span>Standards Ready!</span>
                <p>Yellow color developed. Proceed to Part III: Measurement</p>
              </div>
            )}
          </div>

          {/* Color legend */}
          <div className="color-legend">
            <span className="legend-title">Phosphate-Vanadomolybdate Complex:</span>
            <div className="legend-gradient">
              <div className="legend-bar" />
              <div className="legend-labels">
                <span>2.0{'×'}10{'⁻⁵'} M</span>
                <span>7.5{'×'}10{'⁻⁴'} M</span>
              </div>
            </div>
          </div>

          {/* Bench surface */}
          <div className="bench-surface" />
        </div>
      </div>

      {/* Info Box */}
      {showInfo && (
        <InfoBox
          step={showInfo}
          experimentId="exp3"
          stepInfo={getInfoContent(showInfo)}
          onClose={() => setShowInfo(null)}
        />
      )}

      <style>{`
        .part2-standards {
          display: flex;
          gap: 16px;
          flex: 1;
          width: 100%;
          min-height: 0;
          padding: 16px;
        }

        .steps-panel {
          width: 240px;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .steps-header {
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
        }

        .steps-header h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .steps-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }

        .step-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          margin-bottom: 4px;
          transition: background 0.15s;
        }

        .step-item.active {
          background: rgba(255, 90, 54, 0.15);
        }

        .step-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          flex-shrink: 0;
          background: var(--border);
          color: var(--text-muted);
        }

        .step-number.done {
          background: var(--secondary);
          color: white;
        }

        .step-number.current {
          background: var(--primary);
          color: white;
        }

        .step-content {
          flex: 1;
          min-width: 0;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-title {
          font-size: 12px;
          font-weight: 600;
        }

        .step-title.highlight {
          color: var(--primary-light);
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
          border: none;
          cursor: pointer;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }

        .step-hint {
          font-size: 11px;
          color: var(--text-secondary);
          margin-top: 4px;
          display: block;
        }

        .lab-bench {
          flex: 1;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: auto;
        }

        .bench-header {
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          padding: 12px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: white;
        }

        .bench-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .bench-title h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .bench-progress {
          font-size: 12px;
          opacity: 0.9;
        }

        .reset-part-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 6px;
          border: 1px solid rgba(255,255,255,0.3);
          background: rgba(255,255,255,0.1);
          color: white;
          font-size: 11px;
          cursor: pointer;
          opacity: 0.7;
          transition: opacity 0.2s;
        }

        .reset-part-btn:hover {
          opacity: 1;
          background: rgba(255,255,255,0.2);
        }

        .bench-area {
          flex: 1;
          background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%);
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .color-progress {
          position: absolute;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          width: 250px;
          height: 24px;
          background: var(--border);
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .color-bar {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          background: linear-gradient(90deg, #FFFDE7, #FFE678);
          transition: width 0.3s;
        }

        .color-progress span {
          position: relative;
          font-size: 12px;
          font-weight: 600;
          color: #333;
          text-shadow: 0 1px 2px rgba(255,255,255,0.5);
        }

        .equipment-grid {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          margin-bottom: 80px;
        }

        .stock-button {
          background: var(--primary);
          color: white;
          font-size: 14px;
        }

        .stock-button:hover:not(:disabled) {
          background: var(--primary-dark);
          transform: translateY(-2px);
        }

        .stock-button:disabled {
          opacity: 0.5;
          cursor: default;
        }

        .stock-done-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: rgba(0, 168, 107, 0.15);
          border: 1px solid var(--secondary);
          border-radius: 8px;
          color: var(--secondary);
          font-size: 13px;
          font-weight: 600;
        }

        .flasks-row {
          display: flex;
          align-items: flex-end;
          gap: 16px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .flask-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
        }

        .flask-container.unknown {
          margin-left: 30px;
          padding-left: 30px;
          border-left: 2px dashed var(--border);
        }

        .flask-sublabel {
          font-size: 9px;
          color: var(--text-muted);
          margin-top: -5px;
        }

        .flask-check {
          position: absolute;
          top: 5px;
          right: 5px;
          width: 20px;
          height: 20px;
          background: var(--secondary);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .reagent-drop {
          position: absolute;
          top: -20px;
          color: #FFE135;
          animation: drop 0.4s ease-out forwards;
        }

        @keyframes drop {
          0% {
            transform: translateY(-20px);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateY(30px);
            opacity: 0;
          }
        }

        .reagent-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .equipment-label {
          font-size: 10px;
          color: var(--text-secondary);
          text-align: center;
        }

        .equipment-active {
          animation: glow 1.5s ease-in-out infinite;
        }

        @keyframes glow {
          0%, 100% { filter: drop-shadow(0 0 8px var(--primary)); }
          50% { filter: drop-shadow(0 0 15px var(--primary)); }
        }

        .action-button {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 24px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
        }

        .timer-button {
          background: #FFB300;
          color: #333;
        }

        .timer-button:hover {
          background: #FFA000;
          transform: translateY(-2px);
        }

        .completion-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 24px;
          background: rgba(0, 168, 107, 0.15);
          border-radius: 12px;
          border: 2px solid var(--secondary);
        }

        .completion-message svg {
          color: var(--secondary);
        }

        .completion-message span {
          font-size: 16px;
          font-weight: 600;
          color: var(--secondary);
        }

        .completion-message p {
          font-size: 12px;
          color: var(--text-secondary);
          margin: 0;
        }

        .color-legend {
          position: absolute;
          bottom: 80px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .legend-title {
          font-size: 10px;
          color: var(--text-muted);
        }

        .legend-gradient {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }

        .legend-bar {
          width: 150px;
          height: 12px;
          background: linear-gradient(90deg, #FFFDE7, #FFE678);
          border-radius: 4px;
          border: 1px solid var(--border);
        }

        .legend-labels {
          display: flex;
          justify-content: space-between;
          width: 150px;
          font-size: 9px;
          color: var(--text-secondary);
        }

        .bench-surface {
          display: none;
        }
      `}</style>
    </div>
  )
}

// Info content for each step
function getInfoContent(step: number) {
  const content: Record<number, { title: string; content: string }> = {
    1: {
      title: 'Stock Solution (1.0 × 10⁻\u00b3 M)',
      content: 'Weigh approx. 0.1360 g of dry KH₂PO₄ accurately (record all 4 decimal places) and dissolve in about 500 mL of deionised water using a 1 L volumetric flask. Add 0.5 mL of concentrated H₂SO₄ and dilute to the mark with water. This gives a stock solution that is 1.0 × 10⁻\u00b3 M in various phosphate species.'
    },
    2: {
      title: 'Calibration Standards',
      content: 'From the 1.0 × 10⁻\u00b3 M stock solution, prepare a series of solutions in 100 mL volumetric flasks: Std 1 = 2.0×10⁻⁵ M, Std 2 = 5.0×10⁻⁵ M, Std 3 = 1.0×10⁻⁴ M, Std 4 = 5.0×10⁻⁴ M, Std 5 = 7.5×10⁻⁴ M. Use C₁V₁ = C₂V₂ to calculate pipetting volumes.'
    },
    3: {
      title: 'Dilution Calculations',
      content: 'Each standard is prepared by diluting the phosphate stock solution (1.0 × 10⁻\u00b3 M) to the desired concentration in a 100 mL volumetric flask. Use C₁V₁ = C₂V₂ to calculate the required pipetting volumes. Accurate pipetting is essential for reliable results.'
    },
    4: {
      title: 'Calibration Range',
      content: 'The standards (2.0×10⁻⁵ to 7.5×10⁻⁴ M) should bracket the expected concentration of the unknown sample. This ensures accurate interpolation from the calibration curve.'
    },
    5: {
      title: 'Upper Concentration Standards',
      content: 'The 5.0×10⁻⁴ M standard represents the upper-middle range of the calibration curve. Prepare carefully using C₁V₁ = C₂V₂.'
    },
    6: {
      title: 'Highest Concentration Standard',
      content: 'The 7.5×10⁻⁴ M standard is the highest concentration in the calibration range. All solutions are now ready for the color-forming reagent.'
    },
    7: {
      title: 'The Vanadomolybdate Reaction',
      content: 'Each point on the calibration curve is obtained by mixing 10 mL of the phosphate solution with 5 mL of the ammonium vanadomolybdate solution and measuring the absorbance at 400 nm. In dilute phosphate solutions, ammonium metavanadate (NH₄VO₃), ammonium molybdate (NH₄)₂MoO₄ and phosphate condense to form an intensely yellow coloured compound. The intensity is directly proportional to the concentration of phosphate. NOTE: Do all readings at the same time, including the cola sample.'
    },
    8: {
      title: 'Color Development Time',
      content: 'Allow 10 minutes for full color development. The yellow phosphovanadomolybdate complex forms completely during this time. Do not wait more than 30 minutes before measuring.'
    }
  }
  return content[step]
}

export default Part2Standards
